import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { BusquedaMunicipioDialogComponent, MunicipioSeleccionado } from 'src/app/presentation/common/busquedas/busqueda-municipio-dialog.component';
import { BuscadorPersonaDialogComponent, PersonaSeleccionada } from 'src/app/presentation/common/busquedas/buscador-persona-dialog.component';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { Perfil } from 'src/app/domain/model/catalogo/perfil';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import { TipoFacturacion } from 'src/app/domain/model/catalogo/tipo-facturacion';
import { Proyecto } from 'src/app/domain/model/catalogo/proyecto';
import { Solucion } from 'src/app/domain/model/catalogo/solucion';
import { EmpleadoRequest } from 'src/app/domain/model/empleado/empleado';
import { YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';
import { sanitizeText, sanitizeEmail } from 'src/app/common/input-utils';

/**
 * Dumb component: "Datos de Persona" de "Crear Empleado/Consultor"
 * (HU1259115).
 *
 * <p>NO reutiliza `DatosPersonaEmpleadoComponent` (el usado por Modificar):
 * ese componente está construido alrededor de un `EmpleadoDetalle` ya
 * existente (identificación/nombre precargados desde el empleado, foto ya
 * almacenada con "Cambiar foto", "Ver detalle Persona" con fetch por
 * `cdpersona`) — ninguno de esos tres aplica en Crear, donde todavía no hay
 * empleado ni foto, y la persona se vincula recién en este formulario vía el
 * buscador `siBuscarPersona` (legacy, solo existe en `CrearEmpleadoViewImpl`,
 * ver punto 1/5 de "Diferencias Crear vs Modificar" de HU1259115.md).
 * Forzar el `@Input() modo` sobre ese componente habría dejado ramas muertas
 * (`detalle`/foto/"ver detalle") condicionadas por `modo==='crear'` sin
 * ningún caso de uso real, así que se optó por un dumb component nuevo que sí
 * comparte los mismos catálogos, dropdowns y estructura de campos
 * condicionales (`esAsesor`) que `DatosPersonaEmpleadoComponent`, para que el
 * resto del formulario (orden de campos, obligatoriedad visual, catálogos)
 * quede idéntico entre Crear y Modificar salvo por el bloque de
 * identificación/foto.</p>
 *
 * <p>Orden de campos verificado contra "Orden de Campos en Pantalla
 * (Crear/Modificar)" de HU1259115.md: Tipo/Número identificación (via
 * buscador) → Nombre → Tipo empleado → Oficina → Correo corporativo →
 * Usuario de red → Perfil → Proyecto → Sincroniza Outlook → Solución → Tipo
 * asesor real → Tipo facturación → Capacidad → Calcula productividad →
 * Municipio residencia/prestación.</p>
 */
@Component({
  selector: 'app-datos-persona-crear-empleado',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FieldsetModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DropdownModule,
    RadioButtonModule,
    TooltipModule,
    BusquedaMunicipioDialogComponent,
    BuscadorPersonaDialogComponent,
  ],
  template: `
    <p-fieldset legend="Datos de Persona" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
        <div class="sm:col-span-2 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Identificación Empleado *</label>
          <div class="flex flex-wrap gap-1 min-w-0 items-center">
            <input pInputText readonly class="flex-1 min-w-0"
              [value]="personaSeleccionada() ? (personaSeleccionada()!.cdtipoIdentificacion + ' - ' + personaSeleccionada()!.nmidentificacion) : ''"
              placeholder="Seleccione una persona" />
            <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
              pTooltip="Buscar persona" (click)="dialogBuscadorVisible = true"></button>
            <button pButton icon="pi pi-times" class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              pTooltip="Limpiar" (click)="personaLimpiada.emit()" [disabled]="!personaSeleccionada()"></button>
            <!-- Legacy: saVerDetalle — solo visible después de seleccionar una persona (auditoría fase 25, no existía en la primera versión de Crear). -->
            @if (personaSeleccionada()) {
              <p-button label="Ver detalle Persona" styleClass="p-button-text p-button-sm"
                [loading]="cargandoPersonaDetalle()" (onClick)="verDetallePersona.emit()"></p-button>
            }
          </div>
        </div>
        <div class="sm:col-span-2 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre Completo</label>
          <input pInputText readonly class="w-full" [value]="personaSeleccionada()?.nombreCompleto ?? ''" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Correo Corporativo</label>
          <input pInputText [(ngModel)]="form().dsemailCorporativo" class="w-full" maxlength="150" type="email"
            pTooltip="Correo Corporativo"
            (input)="form().dsemailCorporativo = sanitizeEmail(form().dsemailCorporativo)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Oficina *</label>
          <p-dropdown [(ngModel)]="oficinaSeleccionada" [options]="oficinas()"
            optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
            [filter]="true" filterBy="descripcion" appendTo="body" [style]="{ width: '100%' }"
            pTooltip="Oficina"></p-dropdown>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Usuario de Red *</label>
          <input pInputText [(ngModel)]="form().dsusuarioRed" class="w-full" maxlength="150"
            (input)="form().dsusuarioRed = sanitizeText(form().dsusuarioRed)" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Empleado *</label>
          <p-dropdown [(ngModel)]="form().cdtipoEmpleado" [options]="tiposEmpleado()"
            optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
            [filter]="true" filterBy="descripcion" appendTo="body" [style]="{ width: '100%' }"
            pTooltip="Tipo de Empleado"
            (onChange)="cambioTipoEmpleado.emit()"></p-dropdown>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sincronización con Outlook</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="snprogOutlook" [value]="YES" [(ngModel)]="form().snprogOutlook" inputId="outlookSi"></p-radioButton>
              <label for="outlookSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="snprogOutlook" [value]="NO" [(ngModel)]="form().snprogOutlook" inputId="outlookNo"></p-radioButton>
              <label for="outlookNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>

        <div>
          <!-- Legacy: lbPerfil.setRequired(true) SOLO dentro del bloque Asesor/DirTecnico/AsesorLineaAt (CrearEmpleadoViewImpl.setCamposRequeridos():846) — nunca incondicional (corregido en auditoría de fidelidad fase 25). -->
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Perfil{{ esAsesor() ? ' *' : '' }}</label>
          <p-dropdown [(ngModel)]="form().cdperfil" [options]="perfiles()"
            optionLabel="dsperfil" optionValue="cdperfil" placeholder="--Seleccione--"
            [filter]="true" filterBy="dsperfil" appendTo="body" [style]="{ width: '100%' }"
            pTooltip="Perfil"></p-dropdown>
        </div>

        @if (esAsesor()) {
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Consultor *</label>
            <p-dropdown [(ngModel)]="form().cdtipoAsesorReal" [options]="tiposAsesorReal()"
              optionLabel="dstipo" optionValue="cdtipoReal" placeholder="--Seleccione--"
              [filter]="true" filterBy="dstipo" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Tipo de Consultor"
              (onChange)="cambioTipoAsesorReal.emit()"></p-dropdown>
          </div>
          <div>
            <!-- Legacy: addChangeHandlerTipoAsesorReal() filtra y autoselecciona este dropdown según Tipo Consultor, quedando deshabilitado salvo para Prevención (auditoría fase 25, ver CrearEmpleadoFacade). -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Facturación *</label>
            <p-dropdown [(ngModel)]="form().cdtipoAsesor" [options]="tiposFacturacionFiltradas()"
              optionLabel="dstipo" optionValue="cdtipo" placeholder="--Seleccione--"
              [disabled]="!tipoFacturacionEditable()"
              [filter]="true" filterBy="dstipo" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Tipo de Facturación"
              (onChange)="cambioTipoFacturacion.emit()"></p-dropdown>
          </div>
          @if (mostrarProgramable()) {
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Programable *</label>
              <div class="flex flex-wrap gap-4 pt-2">
                <div class="flex items-center">
                  <p-radioButton name="riesgosProg" [value]="YES" [(ngModel)]="form().snriesgosProgram" inputId="riesgosProgSi"></p-radioButton>
                  <label for="riesgosProgSi" class="ml-2 text-sm">Sí</label>
                </div>
                <div class="flex items-center">
                  <p-radioButton name="riesgosProg" [value]="NO" [(ngModel)]="form().snriesgosProgram" inputId="riesgosProgNo"></p-radioButton>
                  <label for="riesgosProgNo" class="ml-2 text-sm">No</label>
                </div>
              </div>
            </div>
          }
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Capacidad *</label>
            <p-inputNumber [(ngModel)]="form().nmcapacidad" class="w-full" [useGrouping]="false"
              [min]="0" [max]="999" styleClass="w-full" pTooltip="Capacidad del consultor"></p-inputNumber>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estrategia *</label>
            <p-dropdown [(ngModel)]="form().nmconsecutivoProyecto" [options]="proyectos()"
              optionLabel="dsproyecto" optionValue="nmconsecutivo" placeholder="--Seleccione--"
              [filter]="true" filterBy="dsproyecto" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Proyecto al cual esta asignado el consultor"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Solución *</label>
            <p-dropdown [(ngModel)]="form().nmconsecutivoSolucion" [options]="soluciones()"
              optionLabel="dssolucion" optionValue="nmconsecutivo" placeholder="--Seleccione--"
              [filter]="true" filterBy="dssolucion" appendTo="body" [style]="{ width: '100%' }"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Calcular Productividad *</label>
            <div class="flex flex-wrap gap-4 pt-2">
              <div class="flex items-center">
                <p-radioButton name="calcProd" [value]="YES" [(ngModel)]="form().sncalcProd" inputId="calcProdSi" pTooltip="Calcular Productividad"></p-radioButton>
                <label for="calcProdSi" class="ml-2 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton name="calcProd" [value]="NO" [(ngModel)]="form().sncalcProd" inputId="calcProdNo" pTooltip="Calcular Productividad"></p-radioButton>
                <label for="calcProdNo" class="ml-2 text-sm">No</label>
              </div>
            </div>
          </div>

          <div class="sm:col-span-2 lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Municipio Residencia *</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText [value]="municipioResidenciaDisplay()" readonly class="flex-1 min-w-0" placeholder="Seleccione un municipio" />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar municipio" (click)="dialogMunicipioResidenciaVisible = true"></button>
              <button pButton icon="pi pi-times" class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar" (click)="municipioResidenciaLimpiado.emit()" [disabled]="!municipioResidenciaDisplay()"></button>
            </div>
          </div>
          <div class="sm:col-span-2 lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Municipio Prestación *</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText [value]="municipioPrestacionDisplay()" readonly class="flex-1 min-w-0" placeholder="Seleccione un municipio" />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar municipio" (click)="dialogMunicipioPrestacionVisible = true"></button>
              <button pButton icon="pi pi-times" class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar" (click)="municipioPrestacionLimpiado.emit()" [disabled]="!municipioPrestacionDisplay()"></button>
            </div>
          </div>
        }
      </div>
    </p-fieldset>

    @if (dialogBuscadorVisible) {
      <app-buscador-persona-dialog
        (onSelect)="onPersonaSeleccionada($event)"
        (onClose)="dialogBuscadorVisible = false">
      </app-buscador-persona-dialog>
    }
    @if (dialogMunicipioResidenciaVisible) {
      <app-busqueda-municipio-dialog
        [titulo]="'Búsqueda de Municipio de Residencia'"
        (onSelect)="onMunicipioResidenciaSeleccionado($event)"
        (onClose)="dialogMunicipioResidenciaVisible = false">
      </app-busqueda-municipio-dialog>
    }
    @if (dialogMunicipioPrestacionVisible) {
      <app-busqueda-municipio-dialog
        [titulo]="'Búsqueda de Municipio de Prestación de Servicio'"
        (onSelect)="onMunicipioPrestacionSeleccionado($event)"
        (onClose)="dialogMunicipioPrestacionVisible = false">
      </app-busqueda-municipio-dialog>
    }
  `,
})
export class DatosPersonaCrearEmpleadoComponent {
  readonly form = input.required<EmpleadoRequest>();
  readonly personaSeleccionada = input.required<PersonaSeleccionada | null>();
  readonly esAsesor = input.required<boolean>();
  readonly mostrarProgramable = input.required<boolean>();
  readonly oficinas = input.required<CatalogoOption[]>();
  readonly tiposEmpleado = input.required<CatalogoOption[]>();
  readonly perfiles = input.required<Perfil[]>();
  readonly tiposAsesorReal = input.required<TipoAsesorReal[]>();
  /** Opciones de Tipo de Facturación YA filtradas por el facade según el Tipo Consultor elegido (auditoría fase 25). */
  readonly tiposFacturacionFiltradas = input.required<TipoFacturacion[]>();
  /** Legacy: `lbTipoFacturacion.setEnabled(...)` — solo editable para Tipo Consultor = Prevención. */
  readonly tipoFacturacionEditable = input.required<boolean>();
  readonly proyectos = input.required<Proyecto[]>();
  readonly soluciones = input.required<Solucion[]>();
  readonly municipioResidenciaDisplay = input.required<string>();
  readonly municipioPrestacionDisplay = input.required<string>();
  readonly cargandoPersonaDetalle = input(false);

  readonly personaSeleccionadaEvento = output<PersonaSeleccionada>();
  readonly personaLimpiada = output<void>();
  readonly cambioTipoEmpleado = output<void>();
  /** Legacy: `addChangeHandlerTipoAsesorReal()` — el facade decide filtro/autoselección de Tipo de Facturación. */
  readonly cambioTipoAsesorReal = output<void>();
  readonly cambioTipoFacturacion = output<void>();
  /** Legacy: `activity.verDetallePersona()` — visible solo tras seleccionar una persona (auditoría fase 25). */
  readonly verDetallePersona = output<void>();
  readonly municipioResidenciaSeleccionado = output<MunicipioSeleccionado>();
  readonly municipioResidenciaLimpiado = output<void>();
  readonly municipioPrestacionSeleccionado = output<MunicipioSeleccionado>();
  readonly municipioPrestacionLimpiado = output<void>();

  protected readonly YES = YES_VALUE;
  protected readonly NO = NO_VALUE;
  protected readonly sanitizeText = sanitizeText;
  protected readonly sanitizeEmail = sanitizeEmail;

  dialogBuscadorVisible = false;
  dialogMunicipioResidenciaVisible = false;
  dialogMunicipioPrestacionVisible = false;

  /** Ver Javadoc de `DatosPersonaEmpleadoComponent.oficinaSeleccionada` — mismo bug de tipos (`number` vs `string`) que corrige el puente aquí. */
  protected get oficinaSeleccionada(): string | undefined {
    const codigo = this.form().nmconsecOficina;
    return codigo != null ? String(codigo) : undefined;
  }

  protected set oficinaSeleccionada(codigo: string | undefined) {
    this.form().nmconsecOficina = codigo != null && codigo !== '' ? Number(codigo) : (undefined as unknown as number);
  }

  onPersonaSeleccionada(persona: PersonaSeleccionada): void {
    this.personaSeleccionadaEvento.emit(persona);
    this.dialogBuscadorVisible = false;
  }

  onMunicipioResidenciaSeleccionado(municipio: MunicipioSeleccionado): void {
    this.municipioResidenciaSeleccionado.emit(municipio);
    this.dialogMunicipioResidenciaVisible = false;
  }

  onMunicipioPrestacionSeleccionado(municipio: MunicipioSeleccionado): void {
    this.municipioPrestacionSeleccionado.emit(municipio);
    this.dialogMunicipioPrestacionVisible = false;
  }
}
